import { useState, useEffect, useRef, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { useAuth } from '@/hooks/useAuth'
import { auditLogService, type AuditLog, type ListAuditLogsParams } from '@/services/auditLogService'
import LoadingOverlay from '@/components/ui/LoadingOverlay'
import ErrorDisplay from '@/components/ui/ErrorDisplay'
import CustomizableTable, { type TableColumn } from '@/components/ui/CustomizableTable'
import { HttpError } from '@/lib/httpError'

// Matches RequireOwnerOrAdminOrAuditor on the server. Note this is NOT the
// sidebar's `isOwner`, which also lets `security` through — that role gets 403
// from the audit API.
const AUDIT_READERS = ['owner', 'admin', 'auditor']

const PAGE_SIZE = 25

type Filters = {
  layer: string
  action: string
  outcome: string
  service: string
  actor: string
  from: string
  to: string
}

const EMPTY_FILTERS: Filters = {
  layer: '',
  action: '',
  outcome: '',
  service: '',
  actor: '',
  from: '',
  to: '',
}

// Preset ranges. `slice(0, 10)` gives the YYYY-MM-DD an <input type="date">
// expects and the API accepts, so no date library is needed anywhere.
function daysAgo(n: number): string {
  return new Date(Date.now() - n * 86400000).toISOString().slice(0, 10)
}

function today(): string {
  return new Date().toISOString().slice(0, 10)
}

export default function AuditLogsPage() {
  const { t } = useTranslation('auditLogs')
  const { user: currentUser, refreshAuth } = useAuth()

  const [logs, setLogs] = useState<AuditLog[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  const [currentPage, setCurrentPage] = useState(1)
  const [totalItems, setTotalItems] = useState(0)

  const [filters, setFilters] = useState<Filters>(EMPTY_FILTERS)
  const [searchQuery, setSearchQuery] = useState('')

  // Mirrored so fetchLogs stays stable across renders.
  const filtersRef = useRef(filters)
  const searchRef = useRef(searchQuery)
  const pageRef = useRef(currentPage)
  useEffect(() => { filtersRef.current = filters }, [filters])
  useEffect(() => { searchRef.current = searchQuery }, [searchQuery])
  useEffect(() => { pageRef.current = currentPage }, [currentPage])

  const hasAccess = !!currentUser?.platform_role && AUDIT_READERS.includes(currentUser.platform_role)

  const buildParams = (page: number): ListAuditLogsParams => ({
    ...filtersRef.current,
    q: searchRef.current,
    limit: PAGE_SIZE,
    offset: (page - 1) * PAGE_SIZE,
  })

  const fetchLogs = useCallback(async (page?: number) => {
    const actualPage = page ?? pageRef.current
    setLoading(true)
    setError('')

    try {
      const response = await auditLogService.listLogs(buildParams(actualPage))
      setLogs(response.logs || [])
      setTotalItems(response.total || 0)
      setCurrentPage(actualPage)
    } catch (err) {
      if (err instanceof HttpError && err.status === 401) {
        try {
          await refreshAuth()
          return fetchLogs(actualPage)
        } catch {
          setError(t('errors.sessionExpired', 'Your session expired. Sign in again.'))
          setLoading(false)
          return
        }
      }
      const message = err instanceof Error ? err.message : 'errors.operationFailed'
      setError(message.startsWith('errors.') ? t(message, message) : message)
    } finally {
      setLoading(false)
    }
  }, [refreshAuth, t])

  useEffect(() => {
    if (hasAccess) {
      fetchLogs(1)
    } else {
      setLoading(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasAccess])

  const applyFilters = (next: Partial<Filters>) => {
    const merged = { ...filtersRef.current, ...next }
    filtersRef.current = merged
    setFilters(merged)
    fetchLogs(1)
  }

  const clearFilters = () => {
    filtersRef.current = EMPTY_FILTERS
    searchRef.current = ''
    setFilters(EMPTY_FILTERS)
    setSearchQuery('')
    fetchLogs(1)
  }

  const handleSearchChange = (query: string) => {
    searchRef.current = query
    setSearchQuery(query)
    fetchLogs(1)
  }

  const handleExport = () => {
    window.location.href = auditLogService.exportUrl({ ...filters, q: searchQuery })
  }

  // A user agent is 100+ characters of build metadata around one useful word.
  // The table shows the word; the tooltip, Detail and the export keep the rest.
  function shortAgent(raw?: string): string {
    if (!raw) return '—'
    const browser = raw.match(/(Firefox|Edg|Chrome|Safari)\/([0-9]+)/)
    if (browser) {
      const name = browser[1] === 'Edg' ? 'Edge' : browser[1]
      const os = /Macintosh/.test(raw) ? 'macOS' : /Windows/.test(raw) ? 'Windows' : /Linux|X11/.test(raw) ? 'Linux' : ''
      return os ? `${name} ${browser[2]} · ${os}` : `${name} ${browser[2]}`
    }
    const tool = raw.match(/^([A-Za-z-]+)[/ ]([0-9.]+)/)
    return tool ? `${tool[1]} ${tool[2]}` : raw.slice(0, 24)
  }

  const baseColumns: TableColumn<AuditLog>[] = [
    {
      id: 'timestamp',
      label: t('table.timestamp', 'When'),
      accessor: 'timestamp',
      alwaysVisible: true,
      render: (_value, row) => (
        <span className="whitespace-nowrap text-sm text-gray-700">
          {new Date(row.timestamp).toLocaleString()}
        </span>
      ),
    },
    {
      id: 'actor_account',
      label: t('table.actor', 'Who'),
      accessor: 'actor_account',
      alwaysVisible: true,
      render: (_value, row) => (
        <div className="min-w-0">
          <div className="truncate text-sm font-medium text-text-dark">{row.actor_account || '—'}</div>
          {row.actor_role && <div className="text-xs text-gray-500">{row.actor_role}</div>}
        </div>
      ),
    },
    {
      id: 'action',
      label: t('table.action', 'What'),
      accessor: 'action',
      render: (_value, row) => <span className="font-mono text-xs text-gray-800">{row.action}</span>,
    },
    {
      id: 'outcome',
      label: t('table.outcome', 'Result'),
      accessor: 'outcome',
      render: (_value, row) => (
        <span
          className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${
            row.outcome === 'failure' ? 'bg-red-100 text-red-800' : 'bg-green-100 text-green-800'
          }`}
        >
          {row.outcome === 'failure' ? t('outcome.failure', 'Failure') : t('outcome.success', 'Success')}
        </span>
      ),
    },
    {
      id: 'source_ip',
      label: t('table.source', 'From'),
      accessor: 'source_ip',
      render: (_value, row) => <span className="font-mono text-xs text-gray-600">{row.source_ip || '—'}</span>,
    },
    {
      // A browser and a script both come from the same address once the request
      // has crossed the ingress, so the agent is what tells them apart. Shown
      // short; the full string is in Detail and in the CSV export.
      id: 'user_agent',
      label: t('table.userAgent', 'Agent'),
      accessor: 'user_agent',
      defaultVisible: false,
      render: (_value, row) => (
        <span className="text-xs text-gray-600" title={row.user_agent || ''}>
          {shortAgent(row.user_agent)}
        </span>
      ),
    },
    {
      id: 'service',
      label: t('table.service', 'Service'),
      accessor: 'service',
    },
    {
      id: 'target_name',
      label: t('table.target', 'Target'),
      accessor: 'target_name',
      defaultVisible: false,
      render: (_value, row) => <span className="text-sm text-gray-700">{row.target_name || '—'}</span>,
    },
    {
      id: 'message',
      label: t('table.message', 'Message'),
      accessor: 'message',
      render: (_value, row) => <span className="text-sm text-gray-700">{row.message}</span>,
    },
    {
      id: 'detail',
      label: t('table.detail', 'Detail'),
      accessor: 'detail',
      defaultVisible: false,
      render: (_value, row) =>
        row.detail ? (
          <details className="max-w-md">
            <summary className="cursor-pointer text-xs text-blue-600">{t('table.view', 'View')}</summary>
            <pre className="mt-1 overflow-x-auto rounded bg-gray-50 p-2 text-xs text-gray-700">
              {row.detail}
            </pre>
          </details>
        ) : (
          <span className="text-xs text-gray-400">—</span>
        ),
    },
  ]

  // Every filter is applied by the server. The table's own column filters and
  // sort only see the current page, which on an audit trail would quietly show
  // "4 failed logins" when there were four hundred.
  const columns: TableColumn<AuditLog>[] = baseColumns.map((column) => ({
    ...column,
    filterable: false,
    sortable: false,
  }))

  if (!hasAccess) {
    return (
      <div className="absolute inset-0 flex items-center justify-center">
        <div className="max-w-md rounded-lg border border-gray-200 bg-white p-8 text-center">
          <h2 className="mb-2 text-xl font-semibold text-text-dark">
            {t('accessDenied.title', 'Access Denied')}
          </h2>
          <p className="text-gray-600">
            {t('accessDenied.description', 'Only platform owners, admins and auditors can read the audit log.')}
          </p>
        </div>
      </div>
    )
  }

  const layerTabs = [
    { key: '', label: t('layer.all', 'All') },
    { key: 'audit', label: t('layer.audit', 'Audit') },
    { key: 'activity', label: t('layer.activity', 'Activity') },
  ]

  return (
    <div className="absolute inset-0">
      {loading && !error && <LoadingOverlay message={t('loading', 'Loading audit log...')} fullScreen />}
      {error && !loading && (
        <ErrorDisplay error={error} title={t('errors.title', 'Error')} onRefresh={() => fetchLogs()} fullScreen />
      )}

      <div className="mx-auto flex h-full max-w-7xl flex-col overflow-y-auto px-6 pb-4 pt-6">
        <div className="mb-4 shrink-0">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-text-dark">{t('title', 'Audit Log')}</h1>
              <p className="mt-2 text-gray-600">
                {t('description', 'Who did what, when, from where, and the result.')}
              </p>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={() => fetchLogs()}
                className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                {t('actions.refresh', 'Refresh')}
              </button>
              <button
                onClick={handleExport}
                className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700"
              >
                {t('actions.export', 'Export CSV')}
              </button>
            </div>
          </div>
        </div>

        <div className="mb-4 shrink-0 rounded-lg border border-gray-200 bg-white p-4">
          <div className="mb-3 flex gap-2">
            {layerTabs.map((tab) => (
              <button
                key={tab.key || 'all'}
                onClick={() => applyFilters({ layer: tab.key })}
                className={`rounded-lg px-3 py-1.5 text-sm font-medium ${
                  filters.layer === tab.key
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>

          <div className="flex flex-wrap items-end gap-3">
            <label className="flex flex-col text-xs text-gray-600">
              {t('filters.from', 'From')}
              <input
                type="date"
                value={filters.from}
                max={filters.to || today()}
                onChange={(e) => applyFilters({ from: e.target.value })}
                className="mt-1 rounded border border-gray-300 px-2 py-1.5 text-sm"
              />
            </label>
            <label className="flex flex-col text-xs text-gray-600">
              {t('filters.to', 'To')}
              <input
                type="date"
                value={filters.to}
                min={filters.from}
                max={today()}
                onChange={(e) => applyFilters({ to: e.target.value })}
                className="mt-1 rounded border border-gray-300 px-2 py-1.5 text-sm"
              />
            </label>

            <div className="flex gap-1">
              {[
                { days: 1, label: t('filters.last24h', '24h') },
                { days: 7, label: t('filters.last7d', '7d') },
                { days: 30, label: t('filters.last30d', '30d') },
              ].map((preset) => (
                <button
                  key={preset.days}
                  onClick={() => applyFilters({ from: daysAgo(preset.days), to: today() })}
                  className="rounded border border-gray-300 px-2 py-1.5 text-xs text-gray-700 hover:bg-gray-50"
                >
                  {preset.label}
                </button>
              ))}
            </div>

            <label className="flex flex-col text-xs text-gray-600">
              {t('filters.actor', 'User')}
              <input
                type="text"
                value={filters.actor}
                placeholder={t('filters.actorPlaceholder', 'account')}
                onChange={(e) => setFilters({ ...filters, actor: e.target.value })}
                onBlur={(e) => applyFilters({ actor: e.target.value })}
                onKeyDown={(e) => e.key === 'Enter' && applyFilters({ actor: (e.target as HTMLInputElement).value })}
                className="mt-1 rounded border border-gray-300 px-2 py-1.5 text-sm"
              />
            </label>

            <label className="flex flex-col text-xs text-gray-600">
              {t('filters.action', 'Action')}
              <input
                type="text"
                value={filters.action}
                placeholder={t('filters.actionPlaceholder', 'login.failure')}
                onChange={(e) => setFilters({ ...filters, action: e.target.value })}
                onBlur={(e) => applyFilters({ action: e.target.value })}
                onKeyDown={(e) => e.key === 'Enter' && applyFilters({ action: (e.target as HTMLInputElement).value })}
                className="mt-1 rounded border border-gray-300 px-2 py-1.5 text-sm"
              />
            </label>

            <label className="flex flex-col text-xs text-gray-600">
              {t('filters.outcome', 'Result')}
              <select
                value={filters.outcome}
                onChange={(e) => applyFilters({ outcome: e.target.value })}
                className="mt-1 rounded border border-gray-300 px-2 py-1.5 text-sm"
              >
                <option value="">{t('filters.any', 'Any')}</option>
                <option value="success">{t('outcome.success', 'Success')}</option>
                <option value="failure">{t('outcome.failure', 'Failure')}</option>
              </select>
            </label>

            <button
              onClick={clearFilters}
              className="rounded border border-gray-300 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50"
            >
              {t('filters.clear', 'Clear')}
            </button>
          </div>
        </div>

        {!error && (
          <CustomizableTable
            data={logs}
            columns={columns}
            loading={loading}
            emptyMessage={t('empty.description', 'No audit entries match these filters.')}
            pagination={{
              currentPage,
              totalItems,
              pageSize: PAGE_SIZE,
              onPageChange: fetchLogs,
            }}
            searchMode="server"
            onSearchChange={handleSearchChange}
          />
        )}
      </div>
    </div>
  )
}
